/**
 * Enhanced Mega Menu JavaScript
 * Adds interactive functionality to the custom mega menu system
 */

class MegaMenuEnhancer {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenuToggles();
    this.setupKeyboardNavigation();
    this.setupTouchEvents();
    this.setupLoadingStates();
    this.setupCollapsibleSections();
  }

  // Mobile menu collapsible sections
  setupCollapsibleSections() {
    if (window.innerWidth <= 740) {
      const columnHeaders = document.querySelectorAll('.mega-menu__column-header');
      
      columnHeaders.forEach(header => {
        header.addEventListener('click', (e) => {
          e.preventDefault();
          const column = header.closest('.mega-menu__column');
          column.classList.toggle('expanded');
        });
      });
    }
  }

  // Mobile menu toggle functionality
  setupMobileMenuToggles() {
    const menuItems = document.querySelectorAll('.header__linklist-item.has-mega-menu');
    
    menuItems.forEach(item => {
      const link = item.querySelector('.header__linklist-link');
      const megaMenu = item.querySelector('.mega-menu');
      
      if (link && megaMenu && window.innerWidth <= 740) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          item.classList.toggle('mobile-menu-open');
          
          // Close other open menus
          menuItems.forEach(otherItem => {
            if (otherItem !== item) {
              otherItem.classList.remove('mobile-menu-open');
            }
          });
        });
      }
    });
  }

  // Keyboard navigation support
  setupKeyboardNavigation() {
    const menuLinks = document.querySelectorAll('.mega-menu__link, .header__linklist-link');
    
    menuLinks.forEach(link => {
      link.addEventListener('keydown', (e) => {
        const megaMenu = link.closest('.header__linklist-item')?.querySelector('.mega-menu');
        
        switch (e.key) {
          case 'Enter':
          case ' ':
            if (megaMenu && e.target === link) {
              e.preventDefault();
              this.toggleMegaMenu(megaMenu);
            }
            break;
          case 'Escape':
            if (megaMenu) {
              this.closeMegaMenu(megaMenu);
              link.focus();
            }
            break;
          case 'ArrowDown':
            if (megaMenu && e.target === link) {
              e.preventDefault();
              const firstMegaLink = megaMenu.querySelector('.mega-menu__link');
              if (firstMegaLink) firstMegaLink.focus();
            }
            break;
        }
      });
    });
  }

  // Touch events for better mobile experience
  setupTouchEvents() {
    let touchStartY = 0;
    
    document.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
    });

    document.addEventListener('touchend', (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      
      // Close mega menu on upward swipe
      if (deltaY > 50) {
        const openMenus = document.querySelectorAll('.mobile-menu-open');
        openMenus.forEach(menu => {
          menu.classList.remove('mobile-menu-open');
        });
      }
    });
  }

  // Loading states for dynamic content
  setupLoadingStates() {
    const megaMenus = document.querySelectorAll('.mega-menu');
    
    megaMenus.forEach(menu => {
      const productGrid = menu.querySelector('.mega-menu__products');
      
      if (productGrid) {
        // Add loading state when hovering for the first time
        let hasLoaded = false;
        const parentItem = menu.closest('.header__linklist-item');
        
        parentItem?.addEventListener('mouseenter', () => {
          if (!hasLoaded) {
            menu.classList.add('mega-menu--loading');
            
            // Simulate loading time (replace with actual AJAX if needed)
            setTimeout(() => {
              menu.classList.remove('mega-menu--loading');
              hasLoaded = true;
            }, 500);
          }
        });
      }
    });
  }

  // Helper methods
  toggleMegaMenu(megaMenu) {
    const isHidden = megaMenu.hasAttribute('hidden');
    
    if (isHidden) {
      megaMenu.removeAttribute('hidden');
      megaMenu.setAttribute('aria-expanded', 'true');
    } else {
      megaMenu.setAttribute('hidden', '');
      megaMenu.setAttribute('aria-expanded', 'false');
    }
  }

  closeMegaMenu(megaMenu) {
    megaMenu.setAttribute('hidden', '');
    megaMenu.setAttribute('aria-expanded', 'false');
  }

  // Close menus when clicking outside
  closeOnOutsideClick() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header__linklist-item')) {
        const openMenus = document.querySelectorAll('.mobile-menu-open');
        openMenus.forEach(menu => {
          menu.classList.remove('mobile-menu-open');
        });
      }
    });
  }

  // Recalculate on window resize
  handleResize() {
    window.addEventListener('resize', () => {
      if (window.innerWidth > 740) {
        // Reset mobile states on desktop
        const mobileOpenMenus = document.querySelectorAll('.mobile-menu-open');
        mobileOpenMenus.forEach(menu => {
          menu.classList.remove('mobile-menu-open');
        });
        
        const expandedColumns = document.querySelectorAll('.mega-menu__column.expanded');
        expandedColumns.forEach(column => {
          column.classList.remove('expanded');
        });
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenuEnhancer();
});

// Add some utility CSS via JavaScript for enhanced interactions
const enhancementStyles = `
  <style>
    .mega-menu__link:focus-visible {
      outline: 2px solid #0066cc;
      outline-offset: 2px;
      background: rgba(0, 102, 204, 0.1);
    }
    
    .mega-menu__column.expanded .mega-menu__column-header:after {
      transform: rotate(45deg);
    }
    
    .header__linklist-item.mobile-menu-open .header__linklist-link {
      background: rgba(0, 0, 0, 0.05);
    }
    
    @media (prefers-reduced-motion: reduce) {
      .mega-menu__column.expanded .mega-menu__links {
        transition: none;
      }
    }
  </style>
`;

document.head.insertAdjacentHTML('beforeend', enhancementStyles);